import React, { useEffect, useRef } from 'react';
import { X, Send, Bot, Zap, MousePointerClick, MessageSquarePlus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { AssistantChatMessage, ApiAction } from './types';
import FeedbackForm from './FeedbackForm';
import { BTN } from '../../styles/tokens';
import { RunningLoader } from '@/components/RunningLoader';

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  messages: AssistantChatMessage[];
  isThinking: boolean;
  onSend: (text: string) => void;
  onExecutePendingAction: (msgId: string, action: ApiAction) => void;
  currentPageLabel: string;
  /** 覆蓋預設的 bottom-24 right-6 定位，用於可拖曳版本 */
  positionStyle?: React.CSSProperties;
  /** 開啟時直接進入回饋模式 */
  openInFeedbackMode?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  onClose,
  messages,
  isThinking,
  onSend,
  onExecutePendingAction,
  currentPageLabel,
  positionStyle,
  openInFeedbackMode = false,
}) => {
  const [input, setInput] = React.useState('');
  const [isFeedbackMode, setIsFeedbackMode] = React.useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 當視窗開啟時，若指定進入回饋模式則切換
  useEffect(() => {
    if (isOpen) {
      setIsFeedbackMode(openInFeedbackMode);
    }
  }, [isOpen, openInFeedbackMode]);

  useEffect(() => {
    if (isOpen && !isFeedbackMode) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isFeedbackMode]);

  useEffect(() => {
    if (isOpen && !isFeedbackMode) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, isFeedbackMode]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isThinking) return;
    setInput('');
    onSend(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-50 w-[calc(100vw-1rem)] sm:w-96 h-[540px] max-h-[85vh] flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/60 overflow-hidden animate-in slide-in-from-left-4 duration-200 bottom-2 left-2 lg:bottom-4 lg:left-[17rem]"
      style={positionStyle}
    >

      {/* 回饋入口列（僅在對話模式顯示，位於 Header 上方） */}
      {!isFeedbackMode && (
        <button
          onClick={() => setIsFeedbackMode(true)}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800/40 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex-shrink-0"
        >
          <MessageSquarePlus size={15} />
          我要回饋
        </button>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-100/80 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/60 flex-shrink-0">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600/20 text-blue-500 dark:text-blue-400">
          <Bot size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">AI 小幫手</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">目前頁面：{currentPageLabel}</p>
        </div>

        <button
          onClick={onClose}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          <X size={15} />
        </button>
      </div>

      {/* 回饋模式 */}
      {isFeedbackMode ? (
        <FeedbackForm
          onBack={() => setIsFeedbackMode(false)}
          currentPageLabel={currentPageLabel}
        />
      ) : (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3 pb-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                  <Bot size={22} className="text-blue-500 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">嗨！我是 AI 小幫手</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
                    我可以幫您切換頁面、填寫表單、選擇設定。<br />
                    試著說「帶我去快速生成」或「功能說明」！
                  </p>
                </div>
              </div>
            )}

            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mt-0.5">
                    <Bot size={13} className="text-blue-500 dark:text-blue-400" />
                  </div>
                )}

                <div className={`flex flex-col gap-1.5 max-w-[78%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words ${
                      msg.role === 'user'
                        ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700/50 rounded-tr-sm'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700/50 rounded-tl-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="font-semibold text-slate-800 dark:text-slate-100">{children}</strong>,
                          ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>,
                          li: ({ children }) => <li className="text-slate-600 dark:text-slate-300">{children}</li>,
                          h1: ({ children }) => <h1 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-1">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-0.5">{children}</h3>,
                          code: ({ children }) => <code className="bg-slate-200 dark:bg-slate-700 rounded px-1 text-xs font-mono text-blue-600 dark:text-blue-300">{children}</code>,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>

                  {/* 已執行操作標籤 */}
                  {msg.executedLabels && msg.executedLabels.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {msg.executedLabels.map((label, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 border border-emerald-300/60 dark:border-emerald-700/30"
                        >
                          <Zap size={9} />
                          {label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 待確認操作按鈕 */}
                  {msg.pendingActions && msg.pendingActions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {msg.pendingActions.map((action, i) => (
                        <button
                          key={i}
                          onClick={() => onExecutePendingAction(msg.id, action)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border border-blue-300/60 dark:border-blue-600/40 hover:bg-blue-200 dark:hover:bg-blue-800/50 hover:text-blue-700 dark:hover:text-blue-200 hover:border-blue-400 dark:hover:border-blue-500/60 transition-colors"
                        >
                          <MousePointerClick size={10} />
                          {action.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isThinking && (
              <div className="flex gap-2">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <Bot size={13} className="text-blue-400" />
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 rounded-2xl rounded-tl-sm px-3.5 py-2 flex items-center text-slate-500 dark:text-slate-400">
                  <RunningLoader size="sm" label="思考中" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700/60 bg-slate-50/80 dark:bg-slate-800/50 px-3 py-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="輸入訊息（Enter 送出，Shift+Enter 換行）"
                rows={1}
                className="flex-1 resize-none bg-white dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/20 transition-colors max-h-28 overflow-y-auto scrollbar-thin"
                style={{ minHeight: '40px' }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isThinking}
                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl ${BTN.primary}`}
              >
                <Send size={15} />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1.5 px-1">
              AI 小幫手 — 回覆可能有延遲
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWindow;
